import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FaPlus,
  FaSync,
  FaTags,
  FaTimes,
  FaCircle,
  FaEdit,
  FaCheckCircle,
  FaChevronLeft,
  FaChevronRight
} from 'react-icons/fa';
import Sidebar from '../components/Sidebar';
import Navbar from '../components/Navbar';
import { getActiveToken } from '../utils/authStorage';

const API_BASE = process.env.REACT_APP_AUTH_SERVICE_URL || 'http://localhost:3000/api';

export default function Tags() {
  const navigate = useNavigate();

  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [activeFilter, setActiveFilter] = useState('all');
  const [showForm, setShowForm] = useState(false);
  const [editingTag, setEditingTag] = useState(null);
  const [formData, setFormData] = useState({ name: '', slug: '', active: true });
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(8);

  useEffect(() => {
    if (!error) return undefined;
    const timer = setTimeout(() => setError(''), 5000);
    return () => clearTimeout(timer);
  }, [error]);

  useEffect(() => {
    if (!success) return undefined;
    const timer = setTimeout(() => setSuccess(''), 5000);
    return () => clearTimeout(timer);
  }, [success]);

  const getToken = () => getActiveToken() || null;

  const authHeaders = () => {
    const token = getToken();
    const headers = { 'Content-Type': 'application/json' };
    if (token) headers.Authorization = `Bearer ${token}`;
    return headers;
  };

  const handleUnauthorized = (message = 'Session expirée. Veuillez vous reconnecter.') => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    sessionStorage.removeItem('token');
    sessionStorage.removeItem('user');
    navigate('/login');
    throw new Error(message);
  };

  const buildTagsUrl = () => {
    const base = API_BASE.replace(/\/$/, '');
    if (activeFilter === 'active') return `${base}/tags?active=true`;
    if (activeFilter === 'inactive') return `${base}/tags?active=false`;
    return `${base}/tags`;
  };

  const fetchTags = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch(buildTagsUrl(), { headers: authHeaders() });
      const data = await res.json().catch(() => ({}));

      if (res.status === 401 || res.status === 403) {
        handleUnauthorized(data?.message);
      }

      if (!res.ok || data?.success === false) {
        throw new Error(data?.message || `Erreur ${res.status}`);
      }

      setTags(Array.isArray(data?.tags) ? data.tags : []);
      setCurrentPage(1);
    } catch (err) {
      setError(err.message || 'Erreur lors du chargement des tags');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTags();
  }, [activeFilter]);

  const resetForm = () => {
    setFormData({ name: '', slug: '', active: true });
    setEditingTag(null);
    setShowForm(false);
  };

  const openCreateModal = () => {
    setEditingTag(null);
    setFormData({ name: '', slug: '', active: true });
    setShowForm(true);
  };

  const openEditModal = (tag) => {
    setEditingTag(tag);
    setFormData({
      name: tag?.name || '',
      slug: tag?.slug || '',
      active: tag?.active !== false
    });
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    setSuccess('');

    const payload = {
      name: formData.name.trim()
    };

    if (formData.slug.trim()) payload.slug = formData.slug.trim();
    if (editingTag) payload.active = !!formData.active;

    const isUpdate = !!editingTag;
    const base = API_BASE.replace(/\/$/, '');
    const url = isUpdate ? `${base}/tags/${editingTag._id}` : `${base}/tags`;
    const method = isUpdate ? 'PUT' : 'POST';

    try {
      const res = await fetch(url, {
        method,
        headers: authHeaders(),
        body: JSON.stringify(payload)
      });
      const data = await res.json().catch(() => ({}));

      if (res.status === 401 || res.status === 403) {
        handleUnauthorized(data?.message);
      }

      if (!res.ok || data?.success === false) {
        throw new Error(data?.message || `Erreur ${res.status}`);
      }

      setSuccess(isUpdate ? 'Tag modifié avec succès' : 'Tag créé avec succès');
      resetForm();
      fetchTags();
    } catch (err) {
      setError(err.message || 'Erreur lors de l’enregistrement');
    } finally {
      setSaving(false);
    }
  };

  const stats = useMemo(() => {
    const total = tags.length;
    const active = tags.filter((tag) => tag.active !== false).length;
    const inactive = total - active;
    return { total, active, inactive };
  }, [tags]);

  const totalPages = Math.max(1, Math.ceil(tags.length / itemsPerPage));

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  const paginatedTags = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return tags.slice(start, start + itemsPerPage);
  }, [tags, currentPage, itemsPerPage]);

  const startIndex = tags.length === 0 ? 0 : (currentPage - 1) * itemsPerPage + 1;
  const endIndex = Math.min(currentPage * itemsPerPage, tags.length);

  const goToPage = (page) => {
    const safePage = Math.max(1, Math.min(page, totalPages));
    setCurrentPage(safePage);
  };
  const formatDateSimple = (timestamp) => {
    if (!timestamp) return '—';
    // Parse ISO string directly without timezone conversion
    const match = String(timestamp).match(/^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})/);
    if (!match) return '—';
    const [, year, month, day, hour, minute] = match;
    return `${day}/${month}/${year} ${hour}:${minute}`;
  };
  const getVisiblePages = () => {
    const maxVisible = window.innerWidth < 768 ? 3 : 5;
    let start = Math.max(1, currentPage - Math.floor(maxVisible / 2));
    let end = Math.min(totalPages, start + maxVisible - 1);
    if (end - start + 1 < maxVisible) {
      start = Math.max(1, end - maxVisible + 1);
    }
    return Array.from({ length: end - start + 1 }, (_, i) => start + i);
  };

  const formatDate = (value) => {
    if (!value) return '—';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '—';
    return date.toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (active) =>
    active !== false ? (
      <span className="inline-flex items-center rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-semibold text-emerald-700 ring-1 ring-emerald-200/70">
        <FaCircle className="mr-1 h-2 w-2" />
        Actif
      </span>
    ) : (
      <span className="inline-flex items-center rounded-full bg-rose-50 px-2.5 py-1 text-xs font-semibold text-rose-700 ring-1 ring-rose-200/70">
        <FaCircle className="mr-1 h-2 w-2" />
        Inactif
      </span>
    );

  const hasActiveFilters = activeFilter !== 'all';

  const TagCard = ({ tag }) => (
    <div className="rounded-xl border border-slate-200/80 bg-white p-4 shadow-sm ring-1 ring-slate-900/5 transition hover:shadow-md">
      <div className="mb-3 flex items-start justify-between gap-3">
        <div className="flex min-w-0 flex-1 items-start gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-blue-50 text-blue-600">
            <FaTags className="h-4 w-4" />
          </div>
          <div className="min-w-0">
            <div className="truncate text-sm font-semibold text-slate-900">{tag.name || '—'}</div>
            {tag.slug ? (
              <div className="mt-0.5 truncate font-mono text-[11px] text-slate-500">{tag.slug}</div>
            ) : null}
            <div className="mt-1 text-[11px] tabular-nums text-slate-500">
              Créé le {formatDateSimple(tag.createdAt)}
            </div>
          </div>
        </div>
        {getStatusBadge(tag.active)}
      </div>
      <div className="flex justify-end border-t border-slate-100 pt-3">
        <button
          onClick={() => openEditModal(tag)}
          className="inline-flex items-center rounded-lg bg-blue-50 px-3 py-1.5 text-xs font-medium text-blue-700 transition hover:bg-blue-100"
        >
          <FaEdit className="mr-1" />
          Modifier
        </button>
      </div>
    </div>
  );

  const PaginationComponent = () => {
    if (tags.length === 0) return null;
    const pages = getVisiblePages();

    return (
      <div className="sticky bottom-0 z-20 flex flex-col items-center justify-between gap-2 border-t border-slate-200/90 bg-white/95 px-4 py-3 backdrop-blur-sm sm:flex-row">
        <div className="order-2 text-sm text-slate-500 sm:order-1">
          Affichage de {startIndex} à {endIndex} sur {tags.length}
        </div>
        <div className="order-1 flex flex-wrap items-center justify-center gap-1.5 sm:order-2">
          <label className="mr-1 text-xs text-slate-500">Par page</label>
          <select
            value={itemsPerPage}
            onChange={(e) => {
              setItemsPerPage(Number(e.target.value));
              setCurrentPage(1);
            }}
            className="rounded-lg border border-slate-200 bg-white px-2 py-1.5 text-xs text-slate-700 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/15"
          >
            <option value={8}>8</option>
            <option value={12}>12</option>
            <option value={20}>20</option>
          </select>
          <button
            onClick={() => goToPage(currentPage - 1)}
            disabled={currentPage === 1}
            className="rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-sm text-slate-600 transition hover:bg-slate-50 disabled:opacity-40"
          >
            <FaChevronLeft className="text-xs" />
          </button>
          {pages.map((page) => (
            <button
              key={page}
              onClick={() => goToPage(page)}
              className={`min-w-[2rem] rounded-lg border px-2.5 py-1.5 text-sm font-medium transition ${
                page === currentPage
                  ? 'border-blue-600 bg-blue-600 text-white'
                  : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-50'
              }`}
            >
              {page}
            </button>
          ))}
          <button
            onClick={() => goToPage(currentPage + 1)}
            disabled={currentPage === totalPages}
            className="rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-sm text-slate-600 transition hover:bg-slate-50 disabled:opacity-40"
          >
            <FaChevronRight className="text-xs" />
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="flex h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      <Sidebar />
      <div className="flex-1 flex flex-col min-w-0">
        <Navbar />

        <div className="bg-white border-b shadow-sm">
          <div className="p-3 sm:p-4 lg:p-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4 gap-3">
              <div className="flex items-center space-x-2">
                <div className="w-8 h-8 bg-gradient-to-r from-blue-600 to-blue-700 rounded-lg flex items-center justify-center">
                  <FaTags className="text-white text-sm" />
                </div>
                <h1 className="text-lg sm:text-xl font-bold text-gray-900">Tags</h1>
              </div>

              <div className="flex items-center gap-2">
                <select
                  value={activeFilter}
                  onChange={(e) => setActiveFilter(e.target.value)}
                  className="px-3 py-2 border rounded-lg text-sm"
                >
                  <option value="all">Tous</option>
                  <option value="active">Actifs</option>
                  <option value="inactive">Inactifs</option>
                </select>
                <button
                  onClick={fetchTags}
                  disabled={loading}
                  className="flex items-center px-3 py-2 border rounded-lg text-xs font-medium hover:bg-gray-50"
                >
                  <FaSync className={`${loading ? 'animate-spin' : ''} mr-1.5 text-xs`} />
                  Actualiser
                </button>
                <button
                  onClick={openCreateModal}
                  className="flex items-center px-3 py-2 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-lg hover:from-blue-700 hover:to-blue-800 transition-all text-xs font-medium shadow-lg hover:shadow-xl"
                >
                  <FaPlus className="mr-1.5 text-xs" />
                  Nouveau tag
                </button>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-2">
              <div className="flex min-w-0 items-center gap-2 rounded-xl border border-slate-200/80 bg-gradient-to-br from-white to-slate-50/90 px-2.5 py-2 shadow-sm ring-1 ring-slate-900/5">
                <FaTags className="h-3.5 w-3.5 shrink-0 text-slate-400" />
                <div className="min-w-0">
                  <p className="truncate text-[10px] font-semibold uppercase tracking-wide text-slate-500">Total</p>
                  <p className="text-sm font-bold tabular-nums tracking-tight text-slate-900">{stats.total}</p>
                </div>
              </div>
              <div className="flex min-w-0 items-center gap-2 rounded-xl border border-emerald-200/70 bg-emerald-50/50 px-2.5 py-2 shadow-sm ring-1 ring-emerald-900/5">
                <FaCircle className="h-3.5 w-3.5 shrink-0 text-emerald-600" />
                <div className="min-w-0">
                  <p className="truncate text-[10px] font-semibold uppercase tracking-wide text-emerald-700/90">Actifs</p>
                  <p className="text-sm font-bold tabular-nums tracking-tight text-emerald-900">{stats.active}</p>
                </div>
              </div>
              <div className="flex min-w-0 items-center gap-2 rounded-xl border border-rose-200/70 bg-rose-50/50 px-2.5 py-2 shadow-sm ring-1 ring-rose-900/5">
                <FaCircle className="h-3.5 w-3.5 shrink-0 text-rose-600" />
                <div className="min-w-0">
                  <p className="truncate text-[10px] font-semibold uppercase tracking-wide text-rose-700/90">Inactifs</p>
                  <p className="text-sm font-bold tabular-nums tracking-tight text-rose-900">{stats.inactive}</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <main className="flex-1 overflow-hidden p-3 sm:p-4 lg:p-6">
          {loading && (
            <div className="flex h-64 items-center justify-center px-4">
              <div className="text-center">
                <div className="mx-auto mb-4 h-12 w-12 animate-spin rounded-full border-2 border-slate-200 border-t-blue-600"></div>
                <p className="text-sm text-gray-600">Chargement des tags...</p>
              </div>
            </div>
          )}

          {!loading && (
            <div className="flex h-full min-h-0 flex-col overflow-hidden rounded-xl border border-slate-200/80 bg-white shadow-sm ring-1 ring-slate-900/5">
              <div className="hidden min-h-0 flex-1 md:block">
                <div className="h-full overflow-auto">
                  <table className="min-w-full">
                    <thead className="sticky top-0 z-10 border-b border-slate-200 bg-slate-50">
                      <tr>
                        <th className="px-5 py-3.5 text-left text-[10px] font-semibold uppercase tracking-[0.12em] text-blue-700">Nom</th>
                        <th className="px-4 py-3.5 text-left text-[10px] font-semibold uppercase tracking-[0.12em] text-blue-700">Statut</th>
                        <th className="px-4 py-3.5 text-left text-[10px] font-semibold uppercase tracking-[0.12em] text-blue-700">Créé le</th>
                        <th className="px-4 py-3.5 text-left text-[10px] font-semibold uppercase tracking-[0.12em] text-blue-700">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100 bg-white">
                      {tags.length === 0 ? (
                        <tr>
                          <td colSpan={4} className="px-6 py-14 text-center">
                            <div className="mx-auto flex max-w-sm flex-col items-center text-slate-500">
                              <div className="mb-3 flex h-14 w-14 items-center justify-center rounded-2xl bg-slate-100">
                                <FaTags className="text-2xl text-slate-400" />
                              </div>
                              <p className="text-sm font-semibold text-slate-700">Aucun tag trouvé</p>
                              <p className="mt-1 text-xs text-slate-500">
                                {hasActiveFilters ? 'Modifiez le filtre de statut.' : 'Créez votre premier tag avec le bouton « Nouveau tag ».'}
                              </p>
                            </div>
                          </td>
                        </tr>
                      ) : (
                        paginatedTags.map((tag, idx) => (
                          <tr key={tag._id} className={`transition-colors hover:bg-blue-50/40 ${idx % 2 === 1 ? 'bg-slate-50/40' : ''}`}>
                            <td className="px-5 py-4 align-top">
                              <div className="flex min-w-0 items-start gap-3">
                                <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-blue-50 text-blue-600">
                                  <FaTags className="h-3.5 w-3.5" />
                                </div>
                                <div className="min-w-0">
                                  <div className="truncate text-sm font-semibold text-slate-900">{tag.name || '—'}</div>
                                  {tag.slug ? (
                                    <div className="mt-0.5 truncate font-mono text-[11px] text-slate-500">{tag.slug}</div>
                                  ) : (
                                    <div className="mt-0.5 text-[11px] text-slate-500">Tag système</div>
                                  )}
                                </div>
                              </div>
                            </td>
                            <td className="px-4 py-4 align-top whitespace-nowrap">
                              {getStatusBadge(tag.active)}
                            </td>
                            <td className="px-4 py-4 align-top whitespace-nowrap text-sm tabular-nums text-slate-500">
                              {formatDateSimple(tag.createdAt)}
                            </td>
                            <td className="px-4 py-4 align-top">
                              <button
                                onClick={() => openEditModal(tag)}
                                className="inline-flex items-center rounded-lg bg-blue-50 px-2.5 py-1.5 text-xs font-medium text-blue-700 transition hover:bg-blue-100"
                              >
                                <FaEdit className="mr-1" />
                                Modifier
                              </button>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="min-h-0 flex-1 overflow-auto p-4 md:hidden">
                {tags.length === 0 ? (
                  <div className="py-12 text-center">
                    <div className="mx-auto mb-3 flex h-14 w-14 items-center justify-center rounded-2xl bg-slate-100">
                      <FaTags className="text-2xl text-slate-400" />
                    </div>
                    <p className="text-sm font-semibold text-slate-700">Aucun tag trouvé</p>
                    <p className="mt-1 text-xs text-slate-500">
                      {hasActiveFilters ? 'Modifiez le filtre de statut.' : 'Créez votre premier tag.'}
                    </p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {paginatedTags.map((tag) => (
                      <TagCard key={tag._id} tag={tag} />
                    ))}
                  </div>
                )}
              </div>

              <PaginationComponent />
            </div>
          )}
        </main>

        {showForm && (
          <div
            className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
            onClick={resetForm}
          >
            <div
              className="bg-white rounded-2xl p-4 sm:p-6 w-full max-w-md shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold text-gray-900">{editingTag ? 'Modifier le tag' : 'Nouveau tag'}</h2>
                <button onClick={resetForm} className="text-gray-400 hover:text-gray-600 p-2">
                  <FaTimes />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Nom *</label>
                  <input
                    required
                    value={formData.name}
                    onChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value }))}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                    placeholder="Ex: promo"
                  />
                </div>

               

                {editingTag && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Statut</label>
                    <select
                      value={formData.active ? 'active' : 'inactive'}
                      onChange={(e) => setFormData((prev) => ({ ...prev, active: e.target.value === 'active' }))}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                    >
                      <option value="active">Actif</option>
                      <option value="inactive">Inactif</option>
                    </select>
                  </div>
                )}

                <div className="flex justify-end gap-3 pt-2">
                  <button
                    type="button"
                    onClick={resetForm}
                    className="px-4 py-2 rounded-lg border border-gray-300 hover:bg-gray-50 text-sm"
                  >
                    Annuler
                  </button>
                  <button
                    type="submit"
                    disabled={saving}
                    className="px-4 py-2 rounded-lg bg-gradient-to-r from-blue-600 to-blue-700 text-white hover:from-blue-700 hover:to-blue-800 text-sm disabled:opacity-60"
                  >
                    {saving ? 'Enregistrement...' : editingTag ? 'Mettre à jour' : 'Créer'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {(error || success) && (
          <div className="fixed bottom-4 right-4 z-[60] space-y-2">
            {error && (
              <div className="bg-gradient-to-r from-red-500 to-red-600 border border-red-300 rounded-xl shadow-2xl p-4 w-full max-w-sm animate-slide-in-right">
                <div className="flex items-start gap-3">
                  <FaTimes className="text-white text-sm mt-0.5" />
                  <p className="text-white text-sm font-medium flex-1">{error}</p>
                  <button
                    onClick={() => setError('')}
                    className="text-white/80 hover:text-white p-1 rounded"
                  >
                    <FaTimes className="text-xs" />
                  </button>
                </div>
              </div>
            )}
            {success && (
              <div className="bg-gradient-to-r from-emerald-500 to-emerald-600 border border-emerald-300 rounded-xl shadow-2xl p-4 w-full max-w-sm animate-slide-in-right">
                <div className="flex items-start gap-3">
                  <FaCheckCircle className="text-white text-sm mt-0.5" />
                  <p className="text-white text-sm font-medium flex-1">{success}</p>
                  <button
                    onClick={() => setSuccess('')}
                    className="text-white/80 hover:text-white p-1 rounded"
                  >
                    <FaTimes className="text-xs" />
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        <style jsx>{`
          @keyframes slide-in-right {
            from { transform: translateX(100%); opacity: 0; }
            to { transform: translateX(0); opacity: 1; }
          }
          .animate-slide-in-right {
            animation: slide-in-right 0.3s ease-out;
          }
        `}</style>
      </div>
    </div>
  );
}
